import { useCallback, useEffect, useRef, useState } from "react";
import init, {
  parse_finalized,
  parse_notarized,
  parse_seed,
} from "./alto_types/alto_types.js";
import { RATE_LIMIT_THRESHOLD, WS_RECONNECT_DELAY } from "./constants";
import { FinalizedJs, NotarizedJs, SeedJs } from "./types";

export interface UseConsensusWebSocketOptions {
  backendUrl: string;
  publicKey: Uint8Array;
  enabled: boolean;
  onSeed: (seed: SeedJs) => void;
  onNotarized: (notarized: NotarizedJs) => void;
  onFinalized: (finalized: FinalizedJs) => void;
}

export interface UseConsensusWebSocketResult {
  isConnected: boolean;
  error: string | null;
  clearError: () => void;
  resetConnection: () => void;
}

export function useConsensusWebSocket({
  backendUrl,
  publicKey,
  enabled,
  onSeed,
  onNotarized,
  onFinalized,
}: UseConsensusWebSocketOptions): UseConsensusWebSocketResult {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const isInitializedRef = useRef(false);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Use refs for callbacks to avoid reconnecting when handlers change
  const onSeedRef = useRef(onSeed);
  const onNotarizedRef = useRef(onNotarized);
  const onFinalizedRef = useRef(onFinalized);

  useEffect(() => {
    onSeedRef.current = onSeed;
  }, [onSeed]);

  useEffect(() => {
    onNotarizedRef.current = onNotarized;
  }, [onNotarized]);

  useEffect(() => {
    onFinalizedRef.current = onFinalized;
  }, [onFinalized]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const resetConnection = useCallback(() => {
    isInitializedRef.current = false;
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      // Close existing connection if disabled
      if (wsRef.current) {
        try {
          const ws = wsRef.current;
          wsRef.current = null;
          ws.close();
        } catch (err) {
          console.error("Error closing WebSocket:", err);
        }
      }
      return;
    }

    // Skip if already initialized
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    const connectWebSocket = () => {
      // Clear any existing reconnection timers
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Close existing connection (if any)
      if (wsRef.current) {
        try {
          const ws = wsRef.current;
          wsRef.current = null;
          ws.close();
        } catch (err) {
          console.error("Error closing existing WebSocket:", err);
        }
      }

      // Create new WebSocket connection
      const wsCreationTime = Date.now();
      const protocol = backendUrl.includes(":") ? "ws" : "wss";
      const ws = new WebSocket(`${protocol}://${backendUrl}/consensus/ws`);
      wsRef.current = ws;
      ws.binaryType = "arraybuffer";

      ws.onopen = () => {
        console.log(`WebSocket connected: ${backendUrl}`);
        setIsConnected(true);
        setError(null);
      };

      ws.onmessage = (event) => {
        const data = new Uint8Array(event.data);
        const kind = data[0];
        const payload = data.slice(1);

        switch (kind) {
          case 0: // Seed
            const seed = parse_seed(publicKey, payload);
            if (seed) onSeedRef.current(seed);
            break;
          case 1: // Notarization
            const notarized = parse_notarized(publicKey, payload);
            if (notarized) onNotarizedRef.current(notarized);
            break;
          case 2: // Finalization
            const finalized = parse_finalized(publicKey, payload);
            if (finalized) onFinalizedRef.current(finalized);
            break;
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
      };

      ws.onclose = (event) => {
        console.error(`WebSocket closed with code: ${event.code}`);
        setIsConnected(false);

        // Check for potential rate limiting (code 1006 is "Abnormal Closure")
        if (event.code === 1006) {
          const timeSinceStarted = Date.now() - wsCreationTime;
          if (timeSinceStarted < RATE_LIMIT_THRESHOLD) {
            setError(
              "Too many connection attempts from your IP. Try connecting again in a few minutes."
            );
            wsRef.current = null;
            return;
          } else {
            setError("Disconnected from server. Reconnecting...");
          }
        }

        // Attempt to reconnect if we still have a reference
        if (wsRef.current === ws) {
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            connectWebSocket();
          }, WS_RECONNECT_DELAY);
        }
      };
    };

    const setup = async () => {
      await init();
      connectWebSocket();
    };

    setup();

    // Cleanup on unmount
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (wsRef.current) {
        const ws = wsRef.current;
        wsRef.current = null;
        try {
          ws.close(1000, "Component unmounting");
        } catch (err) {
          console.error("Error closing WebSocket during cleanup:", err);
        }
      }
    };
  }, [enabled, backendUrl, publicKey]);

  return {
    isConnected,
    error,
    clearError,
    resetConnection,
  };
}
